import React, { MouseEvent, useEffect, useState } from "react";
import styles from "./CalendarDropdown.module.scss";
import moment from "moment";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import CustomDateRangePicker from "../../../customDateRangePicker/CustomDateRangePicker";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { selectBorderColor } from "../../../../redux/slices/enviromentSlice/EnviromentSlice.selectors";
import { Popover } from "@mui/material";
import CustomMenu from "../../../customMenu/CustomMenu";

interface propsI {
  onChangeDate?: (selectedDates: selectedDatesI) => void;
  dates: selectedDatesI;
}

export interface selectedDatesI {
  startDate: moment.Moment | null | undefined;
  endDate: moment.Moment | null | undefined;
}

const CalendarDropdown: React.FC<propsI> = ({ onChangeDate, dates }) => {
  // redux hooks
  const borderColor = useAppSelector(selectBorderColor);
  // const mainColor = useAppSelector(selectColor) || stylesVars.primaryBlue;

  const { t } = useTranslation();

  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [selectedIniDate, setSelectedIniDate] = useState<
    moment.Moment | null | undefined
  >(dates.startDate);
  const [selectedEndDate, setSelectedEndDate] = useState<
    moment.Moment | null | undefined
  >(dates.endDate);
  const [focusedInput, setFocusedInput] = useState<
    "startDate" | "endDate" | null
  >();

  const calendarDropdownEl = useRef<HTMLDivElement>(null);
  const containerEl = useRef<HTMLDivElement>(null);

  const handleClickDate = (focusedInput: "startDate" | "endDate" | null) => {
    setShowDateRangePicker(true);
    setFocusedInput(focusedInput);
  };

  const handleClickOutside = (e?: MouseEvent) => {
    setFocusedInput(undefined);
    setShowDateRangePicker(false);
  };

  const handleClose = () => {
    setFocusedInput(undefined);
    setShowDateRangePicker(false);
  };

  const handleDateChange = (dateData: selectedDatesI) => {
    setSelectedIniDate(dateData.startDate);
    setSelectedEndDate(dateData.endDate);
    if (onChangeDate) onChangeDate(dateData);
  };

  useEffect(() => {
    if (dates.startDate)
      setSelectedIniDate(moment(dates.startDate, "DD-MM-YYYY"));
    if (dates.endDate) setSelectedEndDate(moment(dates.endDate, "DD-MM-YYYY"));
  }, [dates.startDate, dates.endDate]);

  return (
    <div
      className={styles.mainContainer}
      style={{ borderColor: borderColor }}
      ref={containerEl}
    >
      <div className={styles.calendarDropdown} ref={calendarDropdownEl}>
        <div className={styles.icon}>
          <img
            src="https://booking.availroom.com/v2/assets/icons/blue_calendar.svg"
            alt=""
          />
        </div>
        <div className={styles.calendarDropdownContainer}>
          <div
            className={styles.formGroup}
            onClick={() => {
              handleClickDate("startDate");
            }}
          >
            <span className={styles.label}>{t("labels.from")}</span>
            <div className={`${styles.date}`}>
              {dates.startDate
                ? dates.startDate.format("L")
                : t("labels.iniDate")}
            </div>
          </div>
          <div
            className={styles.formGroup}
            onClick={() => handleClickDate("endDate")}
          >
            <span className={styles.label}>{t("labels.to")}</span>
            <div className={`${styles.date}`}>
              {dates.endDate ? dates.endDate.format("L") : t("labels.endDate")}
            </div>
          </div>
        </div>
      </div>
      <CustomMenu
        open={showDateRangePicker}
        anchorEl={containerEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        onClose={() => handleClickOutside()}
      >
        <CustomDateRangePicker
          focusedInput={focusedInput ?? "startDate"}
          onFocusChange={(focusedInput) => {
            setFocusedInput(focusedInput);
            if (!focusedInput) setShowDateRangePicker(false);
          }}
          startDate={selectedIniDate || null}
          endDate={selectedEndDate || null}
          onDateChange={handleDateChange}
          minDate={moment()}
          onClose={() => {
            handleClose();
          }}
        />
      </CustomMenu>
    </div>
  );
};

export default CalendarDropdown;
